import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiData from '../../services/api_data';

/**
 * Fetches the current user's clients and stores them in Redux (sorted).
 * Used by Router so getData() can read from store instead of calling the API every time.
 *
 * Disabled clients are included when the user has opted in via the "Show disabled clients"
 * toggle (persisted as localStorage["showDisabledClients"]). The backend ignores the flag
 * for non-internal users, so it is safe to send unconditionally; we still gate the request
 * here to avoid unnecessary query strings.
 */
export const fetchUserClients = createAsyncThunk(
    'userClients/fetch',
        async (userId, { rejectWithValue }) => {
        try {
            const includeInactive = localStorage.getItem('showDisabledClients') === 'true';
            const response = await apiData.getUserClientsList(userId, includeInactive);
            if (!response || !response[0]) return { userId, clients: [] };
            const sorted = (response[0].usersClientsClients || []).sort(
                (a, b) => (a.client?.clientName || '').localeCompare(b.client?.clientName || '')
            );
            // No client-side active filter: the backend is now the single source of truth on
            // which clients are visible. If the user has opted in, disabled clients arrive
            // here intentionally and the switcher renders them with an (inactive) marker.
            const clients = includeInactive
                ? sorted
                : sorted.filter((c) => c.client && c.client.active === true);
            return { userId, clients };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const userClientsSlice = createSlice({
    name: 'userClients',
    initialState: {
        clients: [],
        userId: null,
        loading: false,
        error: null,
    },
    reducers: {
        clearUserClients: (state) => {
            state.clients = [];
            state.userId = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserClients.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserClients.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.userId = action.payload.userId;
                state.clients = action.payload.clients;
            })
            .addCase(fetchUserClients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error;
            });
    },
});

export const { clearUserClients } = userClientsSlice.actions;
export default userClientsSlice.reducer;
